import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/cc";
import { Users, RefreshCw, CheckCircle2, XCircle, MessageCircleMore } from "lucide-react";

export default function AdminReferenceRequestsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [hrNotes, setHrNotes] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reference-update-requests", { params: statusFilter === "all" ? {} : { status: statusFilter } });
      setRows(data || []);
    } catch { toast.error("Failed to load requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const decide = async (rid, decision) => {
    try {
      await api.post(`/admin/reference-update-requests/${rid}/decide`, { decision, hr_notes: hrNotes[rid] || "" });
      toast.success(`Marked ${decision}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fade-up" data-testid="admin-reference-requests-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow mb-2">Compliance</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Users className="w-8 h-8" /> Reference update requests</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Candidates can ask to swap unresponsive referees. HR Admin approves or rejects.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select className="cc-select py-1.5" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ref-status-filter">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="more_info_requested">More info requested</option>
            <option value="all">All</option>
          </select>
          <button className="btn-ghost" onClick={load} disabled={loading} data-testid="ref-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
      </div>

      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.id} className="cc-card p-5" data-testid={`ref-req-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="font-display font-bold text-lg">{r.candidate_name}</div>
                <div className="text-xs text-[var(--cc-text-soft)]">{r.candidate_email} · {r.job_title}</div>
                <div className="text-xs mt-1">Submitted {fmtDateTime(r.submitted_at)}</div>
              </div>
              <span className={`pill ${r.status === "pending" ? "pill-warning" : r.status === "approved" ? "pill-success" : r.status === "rejected" ? "pill-danger" : "pill-info"}`}>{r.status.replace("_", " ")}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <div className="label-eyebrow mb-2">References to remove</div>
                {r.references_to_remove?.length > 0 ? (
                  <ul className="space-y-1 text-sm">
                    {r.references_to_remove.map((rm, i) => (
                      <li key={i} className="border-l-2 border-red-200 pl-2">
                        <strong>{rm.name || `Reference ${rm.index + 1}`}</strong>
                        {rm.reason && <span className="text-xs text-[var(--cc-text-soft)]"> — {rm.reason}</span>}
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-sm text-[var(--cc-text-soft)]">None</div>}
              </div>
              <div>
                <div className="label-eyebrow mb-2">New references to add</div>
                {r.new_references?.length > 0 ? (
                  <ul className="space-y-2 text-sm">
                    {r.new_references.map((nr, i) => (
                      <li key={i} className="border-l-2 border-emerald-200 pl-2">
                        <strong>{nr.name}</strong> ({nr.relationship || "—"})<br/>
                        <span className="text-xs text-[var(--cc-text-soft)]">{nr.email}{nr.phone ? ` · ${nr.phone}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-sm text-[var(--cc-text-soft)]">None</div>}
              </div>
            </div>

            {r.candidate_notes && (
              <div className="mt-3 p-3 rounded-md bg-[var(--cc-surface)] text-sm italic">"{r.candidate_notes}"</div>
            )}

            {r.status === "pending" && (
              <div className="mt-4 cc-divider pt-4 space-y-2">
                <input className="cc-input text-sm" placeholder="HR notes (optional)" value={hrNotes[r.id] || ""}
                       onChange={(e) => setHrNotes({ ...hrNotes, [r.id]: e.target.value })} data-testid={`ref-notes-${r.id}`} />
                <div className="flex flex-wrap gap-2 justify-end">
                  <button className="btn-ghost" onClick={() => decide(r.id, "more_info")} data-testid={`ref-moreinfo-${r.id}`}>
                    <MessageCircleMore className="w-4 h-4" /> Request more info
                  </button>
                  <button className="btn-secondary" onClick={() => decide(r.id, "reject")} data-testid={`ref-reject-${r.id}`}>
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                  <button className="btn-primary" onClick={() => decide(r.id, "approve")} data-testid={`ref-approve-${r.id}`}>
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                </div>
              </div>
            )}

            {r.hr_notes && <div className="mt-3 text-xs text-[var(--cc-text-soft)]"><strong>HR note:</strong> {r.hr_notes}</div>}
          </div>
        ))}
        {rows.length === 0 && !loading && (
          <div className="cc-card p-10 text-center text-[var(--cc-text-soft)]">No requests in this view.</div>
        )}
      </div>
    </div>
  );
}
